import { Dispatch } from '@reduxjs/toolkit';
import { keyBy, uniqBy } from 'lodash/fp';

import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { SortParams } from 'dashboard/lib/interfaces/SortParams';
import { Environment } from 'dashboard/models/environment';
import { Workflow } from 'dashboard/models/workflow';
import {
  WorkflowRun,
  WorkflowRunsBulkCreateManualMeta,
  WorkflowRunsBulkResumeMeta,
} from 'dashboard/models/workflow-run';
import { WorkflowVersion } from 'dashboard/models/workflow-version';
import {
  setManualRunResults,
  setWorkflowRuns,
  setWorkflows,
  setWorkflowVersions,
  WorkflowRunsFilterParams,
} from 'dashboard/redux/actions/workflow-runs';
import { ApiEndpoint, ApiModel, PaginationParams } from 'lib/interfaces/api';

export enum WorkflowRunPostResourceActions {
  BulkCreateManualRuns = 'bulk-create-manual-runs',
  BulkResumeRuns = 'bulk-resume-runs',
}

export function loadWorkflowRunList({
  currentEnvironment,
  currentPage,
  pageSize,
  filters,
  sortParams,
  abortSignal,
}: {
  currentEnvironment: Environment;
  currentPage?: number;
  pageSize?: number;
  filters: WorkflowRunsFilterParams;
  sortParams?: SortParams;
  abortSignal?: AbortSignal;
}) {
  return async (dispatch: Dispatch) => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const params = createParamsForList(currentEnvironment, filters, sortParams);

    const paramsFull = {
      ...params,
      page: {
        number: currentPage,
        size: pageSize,
      },
      signal: abortSignal,
    };

    const workflowRunsResponse = await SessionClientDashboard.listResource<
      WorkflowRun,
      PaginationParams
    >(ApiEndpoint.WorkflowRun, paramsFull);

    const workflowRuns = workflowRunsResponse.data;
    const paginationParams = workflowRunsResponse.meta;

    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const allWorkflowsList = workflowRunsResponse.included.filter(
      (apiRelationship) => apiRelationship.type === ApiModel.Workflow,
    );
    const workflowsList = uniqBy('id', allWorkflowsList);
    const workflows = keyBy('id', workflowsList) as Record<string, Workflow>;

    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const allWorkflowVersionsList = workflowRunsResponse.included.filter(
      (apiRelationship) => apiRelationship.type === ApiModel.WorkflowVersion,
    );
    const workflowVersionsList = uniqBy('id', allWorkflowVersionsList);
    const workflowVersions = keyBy('id', workflowVersionsList) as Record<string, WorkflowVersion>;

    dispatch(setWorkflows(workflows));
    dispatch(setWorkflowVersions(workflowVersions));
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    dispatch(setWorkflowRuns(workflowRuns, currentEnvironment, paginationParams));
  };
}

function createParamsForList(
  currentEnvironment: Environment,
  workflowRunFilters: WorkflowRunsFilterParams,
  workflowRunSortParams: SortParams,
) {
  const { omni } = workflowRunFilters;
  const filterParams = createFiltersForList(currentEnvironment, workflowRunFilters);
  const { sort, shouldSortDescending } = workflowRunSortParams;

  const params = {
    filter: filterParams,
    // We want to keep omnisearch relevance ordering, instead of sorting
    sort: omni ? '' : `${shouldSortDescending ? '-' : ''}${sort}`,
  };

  return params;
}

export const createFiltersForList = (
  currentEnvironment: Environment,
  workflowRunFilters: WorkflowRunsFilterParams,
) => {
  const {
    completedAtStart,
    completedAtEnd,
    status,
    workflowIds,
    omni,
    workflowVersionIds,
    triggerType,
    simulate,
    triggerObjectId,
  } = workflowRunFilters;

  return {
    'environment-id': currentEnvironment.id,
    status,
    workflowIds,
    workflowVersionIds,
    triggerType,
    omni,
    simulate,
    'completed-at-start': completedAtStart && completedAtStart.toString(),
    'completed-at-end': completedAtEnd && completedAtEnd.toString(),
    'trigger-object-id': triggerObjectId,
  };
};

export function createBulkManualRuns(workflowId: string, objectIds: string[]) {
  return async (dispatch: Dispatch) => {
    const { meta } = await SessionClientDashboard.postResourceSingletonActionNew<
      WorkflowRun,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      undefined,
      WorkflowRunsBulkCreateManualMeta
    >(ApiEndpoint.WorkflowRun, WorkflowRunPostResourceActions.BulkCreateManualRuns, undefined, {
      meta: {
        'workflow-id': workflowId,
        'object-ids': objectIds,
      },
    });

    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    dispatch(setManualRunResults(meta['valid-object-ids'], meta['invalid-object-ids']));
  };
}

export function bulkResumeWorkflowRuns(workflowId: string, workflowRunIds: string[]) {
  return async (dispatch: Dispatch) => {
    const { meta } = await SessionClientDashboard.postResourceSingletonActionNew<
      WorkflowRun,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      undefined,
      WorkflowRunsBulkResumeMeta
    >(ApiEndpoint.WorkflowRun, WorkflowRunPostResourceActions.BulkResumeRuns, undefined, {
      meta: {
        'workflow-id': workflowId,
        'object-ids': workflowRunIds,
      },
    });

    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    dispatch(setManualRunResults(meta['valid-object-ids'], meta['invalid-object-ids']));
  };
}
